import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Checkbox,
  CircularProgress,
  TextField,
  Button,
  TableSortLabel,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/Download";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import startOfMonth from "date-fns/startOfMonth";
import subMonths from "date-fns/subMonths";
import { UserContext } from "../../App";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function getWhatsAppInvoiceUrl() {
  const raw = process.env.REACT_APP_GET_WHATSAPP_INVOICE;
  const url = (typeof raw === "string" ? raw : "").replace(/^['"]|['"]$/g, "").trim();
  return url;
}

function getSendWhatsAppInvoiceUrl() {
  const raw = process.env.REACT_APP_CLIENT_SEND_WATSAPP_INVOICE_URL;
  return (typeof raw === "string" ? raw : "").replace(/^['"]|['"]$/g, "").trim();
}

function getWhatsAppInvoiceHistoryUrl() {
  const raw = process.env.REACT_APP_GET_WHATSAPP_INVOICE_HISTORY;
  return (typeof raw === "string" ? raw : "").replace(/^['"]|['"]$/g, "").trim();
}

function WhatsAppInvoiceTab({ email: emailProp }) {
  const { email: emailFromContext } = useContext(UserContext) || {};
  const email = emailProp ?? emailFromContext ?? "";
  const userEmail = email?.trim() || "noreply@dummy.com";
  const [selectedDate, setSelectedDate] = useState(() => dayjs(startOfMonth(subMonths(new Date(), 1))));
  const [searchMonth, setSearchMonth] = useState(() => {
    const d = new Date();
    const prevMonth = d.getMonth() === 0 ? 11 : d.getMonth() - 1;
    const prevYear = d.getMonth() === 0 ? d.getFullYear() - 1 : d.getFullYear();
    return `${months[prevMonth]} ${prevYear}`;
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("client_name");
  const [order, setOrder] = useState("asc");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    setSearchMonth(`${months[selectedDate.month()]} ${selectedDate.year()}`);
  }, [selectedDate]);

  const fetchWhatsAppInvoice = async () => {
    const baseUrl = getWhatsAppInvoiceUrl();
    if (!baseUrl) return;
    setLoading(true);
    try {
      const y = selectedDate.year();
      const m = selectedDate.month() + 1;
      const monthParam = `${y}-${String(m).padStart(2, "0")}`;
      const url = `${baseUrl}?month=${monthParam}`;
      const res = await axios.get(url);
      const json = res?.data;
      const rawList = Array.isArray(json) ? json : (json?.data ?? json?.whatsapp_invoice_data ?? []);
      const normalized = rawList.map((item) => ({
        ...item,
        invoice_number: item.invoice_number ?? item.invoice_num,
        invoice_amount: item.invoice_amount ?? item.cost,
      }));
      setData(normalized);
    } catch (err) {
      console.error("Error fetching WhatsApp invoice:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWhatsAppInvoice();
  }, [selectedDate]);

  const handleDateChange = (e) => {
    setSelectedDate(e);
  };

  const rowId = (row) => row.client_fk ?? row.unique_id ?? String(row.client_name ?? "");

  const searchLower = search.toLowerCase().trim();
  const filteredData = data.filter((row) => {
    if (!searchLower) return true;
    const client_name = (row.client_name ?? "").toLowerCase();
    const unique_id = String(row.unique_id ?? "").toLowerCase();
    const billing_type = (row.billing_type ?? "").toLowerCase();
    const status = (row.status ?? "").toLowerCase();
    const invoice_number = (row.invoice_number ?? "").toLowerCase();
    const invoice_amount = String(row.invoice_amount ?? "").toLowerCase();
    return (
      client_name.includes(searchLower) ||
      unique_id.includes(searchLower) ||
      billing_type.includes(searchLower) ||
      status.includes(searchLower) ||
      invoice_number.includes(searchLower) ||
      invoice_amount.includes(searchLower)
    );
  });

  const getComparator = (orderByKey, orderDir) => (a, b) => {
    const aVal = a[orderByKey] ?? "";
    const bVal = b[orderByKey] ?? "";
    const aNum = Number(aVal);
    const bNum = Number(bVal);
    const aNumValid = !Number.isNaN(aNum);
    const bNumValid = !Number.isNaN(bNum);
    let cmp = 0;
    if (orderByKey === "invoice_amount" && (aNumValid || bNumValid)) {
      cmp = (aNumValid ? aNum : 0) - (bNumValid ? bNum : 0);
    } else {
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      cmp = aStr.localeCompare(bStr, undefined, { numeric: true });
    }
    if (cmp !== 0) return orderDir === "asc" ? cmp : -cmp;
    const aId = String(rowId(a));
    const bId = String(rowId(b));
    return aId.localeCompare(bId, undefined, { numeric: true });
  };

  const sortedData = [...filteredData].sort(getComparator(orderBy, order));
  const totalRows = sortedData.length;
  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (columnId) => () => {
    const isAsc = orderBy === columnId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnId);
    setPage(0);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const allSelectedOnPage = paginatedData.length > 0 && paginatedData.every((row) => selectedRows.has(rowId(row)));
  const someSelectedOnPage = paginatedData.some((row) => selectedRows.has(rowId(row)));

  const handleSelectAll = (e) => {
    const ids = paginatedData.map((row) => rowId(row));
    if (e.target.checked) {
      setSelectedRows((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });
    } else {
      setSelectedRows((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const handleSelectRow = (id) => (e) => {
    e.stopPropagation();
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const someSelected = selectedRows.size > 0;

  const selectedList = data.filter((row) => selectedRows.has(rowId(row)));

  const handleSendWhatsAppInvoice = () => {
    setConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    const baseUrl = getSendWhatsAppInvoiceUrl();
    if (!baseUrl) {
      setSendError("Send WhatsApp invoice URL is not configured.");
      return;
    }
    const d = selectedDate.clone();
    const startDate = d.startOf("month").format("YYYY-MM-DD");
    const endDate = selectedDate.clone().endOf("month").format("YYYY-MM-DD");
    const payload = selectedList.map((row) => ({
      clientId: row.client_fk,
      startDate,
      endDate,
      invoiceDate: endDate,
      user_email: userEmail,
    }));
    setSending(true);
    setSendError(null);
    try {
      const res = await axios.post(baseUrl, payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (!(res?.status >= 200 && res?.status < 300)) {
        throw new Error(`HTTP ${res?.status}`);
      }
      setConfirmOpen(false);
      setSelectedRows(new Set());
      setSuccessOpen(true);
    } catch (err) {
      console.error("Error sending WhatsApp invoice:", err);
      const msg = err?.response?.data
        ? (typeof err.response.data === "string" ? err.response.data : err.response.data?.message)
        : err?.message;
      setSendError(msg || "Failed to send WhatsApp invoice.");
    } finally {
      setSending(false);
    }
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setSendError(null);
  };

  useEffect(() => {
    setPage(0);
  }, [search, data.length]);

  return (
    <div style={{ padding: "16px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              views={["month", "year"]}
              label="Select Month and Year"
              value={selectedDate}
              onChange={handleDateChange}
              slotProps={{
                textField: { size: "small", sx: { width: "200px" } },
              }}
            />
          </LocalizationProvider>
          {searchMonth && (
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Showing: <strong>{searchMonth}</strong>
            </Typography>
          )}
        </div>
        <TextField
          size="small"
          placeholder="Search all columns..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: "260px" }}
        />
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSendWhatsAppInvoice}
          disabled={!someSelected}
          sx={{
            textTransform: "none",
            fontSize: "14px",
            backgroundColor: "#1e40af",
            "&:hover": { backgroundColor: "#1d4ed8" },
          }}
        >
          Send WhatsApp Invoice
        </Button>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <CircularProgress size={36} />
        </div>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ boxShadow: "0 1px 4px rgba(0,0,0,0.1)", borderRadius: "8px", maxHeight: "calc(100vh - 340px)", overflow: "auto" }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: "48px", backgroundColor: "#1e40af", padding: "10px 8px" }}>
                  <Checkbox
                    indeterminate={someSelectedOnPage && !allSelectedOnPage}
                    checked={allSelectedOnPage}
                    onChange={handleSelectAll}
                    size="small"
                    sx={{ color: "#fff", "&.Mui-checked": { color: "#fff" } }}
                  />
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px" }}>
                  <TableSortLabel active={orderBy === "client_name"} direction={orderBy === "client_name" ? order : undefined} onClick={handleSort("client_name")} sx={{ color: "#fff" }}>
                    Client Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>
                  <TableSortLabel active={orderBy === "unique_id"} direction={orderBy === "unique_id" ? order : undefined} onClick={handleSort("unique_id")} sx={{ color: "#fff" }}>
                    Unique ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>
                  <TableSortLabel active={orderBy === "billing_type"} direction={orderBy === "billing_type" ? order : undefined} onClick={handleSort("billing_type")} sx={{ color: "#fff" }}>
                    Billing Type
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>
                  <TableSortLabel active={orderBy === "status"} direction={orderBy === "status" ? order : undefined} onClick={handleSort("status")} sx={{ color: "#fff" }}>
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>
                  <TableSortLabel active={orderBy === "invoice_number"} direction={orderBy === "invoice_number" ? order : undefined} onClick={handleSort("invoice_number")} sx={{ color: "#fff" }}>
                    Invoice Number
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>
                  <TableSortLabel active={orderBy === "invoice_amount"} direction={orderBy === "invoice_amount" ? order : undefined} onClick={handleSort("invoice_amount")} sx={{ color: "#fff" }}>
                    Invoice Amount
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((row, idx) => {
                  const id = rowId(row);
                  return (
                    <TableRow
                      key={id || idx}
                      sx={{
                        "&:hover": { backgroundColor: "#e8f4fd" },
                        backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8faff",
                      }}
                    >
                      <TableCell padding="checkbox" sx={{ padding: "8px" }}>
                        <Checkbox
                          size="small"
                          checked={selectedRows.has(id)}
                          onChange={handleSelectRow(id)}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px" }}>{row.client_name ?? "-"}</TableCell>
                      <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{row.unique_id ?? "-"}</TableCell>
                      <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{row.billing_type ?? "-"}</TableCell>
                      <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{row.status ?? "-"}</TableCell>
                      <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{row.invoice_number ?? "-"}</TableCell>
                      <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>
                        {row.invoice_amount != null ? `₹${Number(row.invoice_amount).toLocaleString("en-IN")}` : "-"}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}>
                    <Typography variant="body2">No WhatsApp invoice data for {searchMonth}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 15, 20, 50]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: "1px solid #e2e8f0" }}
          />
        </TableContainer>
      )}

      <Dialog open={confirmOpen} onClose={handleCloseConfirm} maxWidth="md" fullWidth>
        <DialogTitle>Confirm Send WhatsApp Invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to send WhatsApp invoice for the following {selectedList.length} client(s):
          </Typography>
          <Table size="small" sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1e40af" }}>
                <TableCell sx={{ color: "#fff", fontWeight: 600, fontSize: "12px", whiteSpace: "nowrap" }}>Client Name</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600, fontSize: "12px", textAlign: "center", whiteSpace: "nowrap" }}>Invoice Number</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600, fontSize: "12px", textAlign: "center", whiteSpace: "nowrap" }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedList.map((row, idx) => (
                <TableRow key={rowId(row) || idx}>
                  <TableCell sx={{ fontSize: "12px", whiteSpace: "nowrap" }}>{row.client_name ?? "-"}</TableCell>
                  <TableCell sx={{ fontSize: "12px", textAlign: "center", whiteSpace: "nowrap" }}>{row.invoice_number ?? "-"}</TableCell>
                  <TableCell sx={{ fontSize: "12px", textAlign: "center", whiteSpace: "nowrap" }}>
                    {row.invoice_amount != null ? `₹${Number(row.invoice_amount).toLocaleString("en-IN")}` : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="body2" sx={{ mt: 2, color: "#64748b" }}>
            Do you want to proceed?
          </Typography>
          {sendError && (
            <Typography variant="body2" sx={{ mt: 1, color: "#dc2626" }}>
              {sendError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseConfirm} color="inherit" disabled={sending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmSend} disabled={sending} sx={{ backgroundColor: "#1e40af", "&:hover": { backgroundColor: "#1d4ed8" } }}>
            {sending ? "Sending..." : "Confirm & Send"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={successOpen} autoHideDuration={4000} onClose={() => setSuccessOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSuccessOpen(false)} severity="success" sx={{ width: "100%" }}>
          WhatsApp invoice sent successfully.
        </Alert>
      </Snackbar>
    </div>
  );
}

function WhatsAppInvoiceHistoryTab() {
  const [selectedDate, setSelectedDate] = useState(() => dayjs(startOfMonth(subMonths(new Date(), 1))));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchHistory = async () => {
    const baseUrl = getWhatsAppInvoiceHistoryUrl();
    if (!baseUrl) {
      setError("REACT_APP_GET_WHATSAPP_INVOICE_HISTORY is not set in .env. Restart the app after adding it.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const y = selectedDate.year();
      const m = selectedDate.month() + 1;
      const monthParam = `${y}-${String(m).padStart(2, "0")}`;
      const url = `${baseUrl}?month=${monthParam}`;
      const res = await axios.get(url);
      const json = res?.data;
      const rawList = Array.isArray(json) ? json : (json?.data ?? json?.whatsapp_invoice_history ?? []);
      const normalized = (rawList || []).map((item) => ({
        ...item,
        client_name: item.client_name,
        billing_type: item.billing_type,
        status: item.status,
        unique_id: item.unique_id,
        invoice_number: item.invoice_number ?? item.invoice_num,
        invoice_sent_by: item.invoice_sent_by,
        invoice_sent_at: item.invoice_sent_at ?? item.last_updated_at,
        invoice_sent_date: item.invoice_sent_at ?? item.last_updated_at,
        client_fk: item.client_fk,
        invoice_amount: item.invoice_amount ?? item.cost,
        invoice_month: item.invoice_month,
      }));
      setData(normalized);
    } catch (err) {
      console.error("Error fetching WhatsApp invoice history:", err);
      setData([]);
      setError(err.message || "Failed to load history. Check console and CORS.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [selectedDate]);

  const filteredData = data.filter(
    (row) =>
      (row.client_name?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (row.unique_id?.toString() || "").toLowerCase().includes(search.toLowerCase()) ||
      (row.invoice_number?.toString() || "").toLowerCase().includes(search.toLowerCase()) ||
      (row.billing_type?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (row.status?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (row.invoice_sent_by?.toLowerCase() || "").includes(search.toLowerCase())
  );

  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= filteredData.length) {
      setPage(0);
    }
  }, [filteredData.length, page, rowsPerPage]);

  const formatTimestamp = (isoStr) => {
    if (!isoStr) return "-";
    try {
      const d = new Date(isoStr);
      if (Number.isNaN(d.getTime())) return isoStr;
      return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch {
      return isoStr;
    }
  };

  const exportToCsv = () => {
    const headers = ["Client Name", "Unique ID", "Billing Type", "Status", "Invoice Number", "Invoice Amount", "Invoice Sent Date", "Invoice Sent By"];
    const escapeCsv = (val) => {
      const s = val == null ? "" : String(val);
      if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const rows = filteredData.map((row) => [
      escapeCsv(row.client_name),
      escapeCsv(row.unique_id),
      escapeCsv(row.billing_type),
      escapeCsv(row.status),
      escapeCsv(row.invoice_number),
      row.invoice_amount != null ? escapeCsv(`₹${Number(row.invoice_amount).toLocaleString("en-IN")}`) : escapeCsv("-"),
      escapeCsv(formatTimestamp(row.invoice_sent_date)),
      escapeCsv(row.invoice_sent_by),
    ]);
    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `whatsapp-invoice-history-${selectedDate.format("YYYY-MM")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: "16px 0" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Month"
            views={["year", "month"]}
            value={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            slotProps={{ textField: { size: "small", sx: { minWidth: 160 } } }}
          />
        </LocalizationProvider>
        <TextField
          size="small"
          placeholder="Search client or unique ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: "240px" }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={exportToCsv}
          disabled={filteredData.length === 0}
          sx={{ textTransform: "none", borderColor: "#1e40af", color: "#1e40af", "&:hover": { borderColor: "#1d4ed8", backgroundColor: "rgba(30, 64, 175, 0.08)" } }}
        >
          Export
        </Button>
      </div>

      {error && (
        <div
          style={{
            padding: "12px 16px",
            marginBottom: "16px",
            backgroundColor: "#fef2f2",
            color: "#991b1b",
            borderRadius: "8px",
            fontSize: "13px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{error}</span>
          <Button size="small" onClick={fetchHistory} sx={{ textTransform: "none" }}>
            Retry
          </Button>
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <CircularProgress size={36} />
        </div>
      ) : (
        <>
        <TableContainer
          component={Paper}
          sx={{ boxShadow: "0 1px 4px rgba(0,0,0,0.1)", borderRadius: "8px", maxHeight: "calc(100vh - 340px)", overflow: "auto" }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px" }}>Client Name</TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>Unique ID</TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>Billing Type</TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>Status</TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>Invoice Number</TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>Invoice Amount</TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>Invoice Sent Date</TableCell>
                <TableCell sx={{ backgroundColor: "#1e40af", color: "#ffffff", fontWeight: 600, fontSize: "13px", textAlign: "center" }}>Invoice Sent By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((row, idx) => (
                  <TableRow
                    key={row.client_fk ?? row.unique_id ?? idx}
                    sx={{
                      "&:hover": { backgroundColor: "#e8f4fd" },
                      backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8faff",
                    }}
                  >
                    <TableCell sx={{ fontSize: "12px" }}>{row.client_name ?? "-"}</TableCell>
                    <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{row.unique_id ?? "-"}</TableCell>
                    <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{row.billing_type ?? "-"}</TableCell>
                    <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{row.status ?? "-"}</TableCell>
                    <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{row.invoice_number ?? "-"}</TableCell>
                    <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>
                      {row.invoice_amount != null ? `₹${Number(row.invoice_amount).toLocaleString("en-IN")}` : "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{formatTimestamp(row.invoice_sent_date) ?? "-"}</TableCell>
                    <TableCell sx={{ fontSize: "12px", textAlign: "center" }}>{row.invoice_sent_by ?? "-"}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}>
                    <Typography variant="body2">
                      {data.length === 0 && !loading
                        ? "No WhatsApp invoices were sent in the selected month. Try picking a different month, or check whether the WhatsApp send flow ran this cycle."
                        : "No matching history."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPageOptions={[10, 15, 20, 50]}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
        </>
      )}
    </div>
  );
}

function WhatsAppInvoice({ isSidebarOpen, email: emailProp }) {
  const { email: emailFromContext } = useContext(UserContext) || {};
  const email = emailProp ?? emailFromContext ?? "";
  const [activeTab, setActiveTab] = useState("whatsapp_invoice");

  const tabs = [
    { id: "whatsapp_invoice", label: "WhatsApp Invoice" },
    { id: "whatsapp_invoice_history", label: "WhatsApp Invoice History" },
  ];

  return (
    <div style={{ padding: "0" }}>
      <div
        style={{
          display: "flex",
          borderBottom: "2px solid #e2e8f0",
          marginBottom: "4px",
        }}
      >
        {tabs.map((tab) => (
          <div
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 24px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? "#1e40af" : "#64748b",
              borderBottom: activeTab === tab.id ? "2px solid #1e40af" : "2px solid transparent",
              marginBottom: "-2px",
              transition: "all 0.15s",
              userSelect: "none",
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>

      <div style={{ paddingTop: "4px" }}>
        {activeTab === "whatsapp_invoice" && <WhatsAppInvoiceTab email={email} />}
        {activeTab === "whatsapp_invoice_history" && <WhatsAppInvoiceHistoryTab />}
      </div>
    </div>
  );
}

export default WhatsAppInvoice;
